import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck } from '@fortawesome/pro-solid-svg-icons'
import './WingsSkills.css'

/**
 * WingsSkills Component
 *
 * Renders a list of skill badges with optional toggle/select behavior.
 * Skills are sorted by frequencyPercentage (highest first) and deduplicated.
 * When `onToggle` is provided, badges become clickable — selected skills show
 * a checkmark and a highlight ring via box-shadow.
 *
 * @component
 * @example
 * // Read-only
 * <WingsSkills skills={careerData.softSkills} variant="soft-skill" />
 *
 * // Selectable
 * <WingsSkills
 *   skills={careerData.softSkills}
 *   variant="soft-skill"
 *   selectedSkills={profileSkills}
 *   onToggle={handleSkillToggle}
 * />
 *
 * @param {Object}   props
 * @param {Array}    props.skills                         - Array of skill objects: `{ name: string, frequencyPercentage: number }`
 * @param {string}   [props.variant='soft-skill']         - WingsBadge color variant ('soft-skill' | 'hard-skill')
 * @param {Array}    [props.selectedSkills=[]]            - Names of currently selected skills
 * @param {Function} [props.onToggle]                     - Called with `skillName` when a badge is clicked
 * @param {number}   [props.maxVisible=9]                 - Number of skills to show before the "View More" button appears
 * @param {number}   [props.minFrequency=5]               - Minimum frequencyPercentage for a skill to be displayed
 * @param {string}   [props.viewMoreLabel='VIEW MORE']    - Label for the expand button
 * @param {string}   [props.viewLessLabel='VIEW LESS']    - Label for the collapse button
 * @param {string}   [props.className='']                 - Additional CSS class for the wrapper
 */
const WingsSkills = ({
  skills = [],
  variant = 'soft-skill',
  selectedSkills = [],
  onToggle,
  maxVisible = 9,
  minFrequency = 5,
  viewMoreLabel = 'VIEW MORE',
  viewLessLabel = 'VIEW LESS',
  className = '',
}) => {
  const [showAll, setShowAll] = useState(false)

  const getSortedUniqueSkills = (rawSkills) => {
    const seen = new Set()
    return [...rawSkills]
      .sort((a, b) => Number(b.frequencyPercentage) - Number(a.frequencyPercentage))
      .filter((skill) => {
        const key = skill.name?.toLowerCase().trim()
        if (seen.has(key)) return false
        seen.add(key)
        return true
      })
  }

  const filtered = getSortedUniqueSkills(skills).filter(
    (skill) => skill.frequencyPercentage > minFrequency
  )

  const visible = showAll ? filtered : filtered.slice(0, maxVisible)
  const hasMore = filtered.length > maxVisible
  const isSelectable = typeof onToggle === 'function'

  return (
    <div className={`wings-skills${className ? ` ${className}` : ''}`}>
      <div className="wings-skills-badges">
        {visible.map((skill) => {
          const inProfile = selectedSkills.includes(skill.name)
          return (
            <button
              key={skill.name}
              type="button"
              className={`wings-skills-badge wings-skills-badge-${variant}${inProfile ? ' wings-skills-badge-selected' : ''}`}
              onClick={isSelectable ? () => onToggle(skill.name) : undefined}
              aria-pressed={isSelectable ? inProfile : undefined}
              title={
                isSelectable
                  ? inProfile
                    ? 'Remove from resume'
                    : 'Save skill to resume'
                  : undefined
              }
            >
              {isSelectable && (
                <span className={`wings-skills-check-wrap${inProfile ? ' wings-skills-check-wrap-visible' : ''}`}>
                  <FontAwesomeIcon icon={faCheck} className="wings-skills-check" />
                </span>
              )}
              {skill.name}
            </button>
          )
        })}
      </div>

      {hasMore && (
        <button
          type="button"
          className="wings-skills-view-more"
          onClick={() => setShowAll((prev) => !prev)}
        >
          {showAll ? viewLessLabel : viewMoreLabel}
        </button>
      )}
    </div>
  )
}

WingsSkills.propTypes = {
  /** Array of skill objects with name and frequencyPercentage */
  skills: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      frequencyPercentage: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    })
  ).isRequired,
  /** WingsBadge color variant */
  variant: PropTypes.oneOf(['soft-skill', 'hard-skill']),
  /** Names of currently selected/toggled skills */
  selectedSkills: PropTypes.arrayOf(PropTypes.string),
  /** Callback fired with the skill name when a badge is clicked */
  onToggle: PropTypes.func,
  /** Max skills shown before the expand button appears */
  maxVisible: PropTypes.number,
  /** Skills below this frequencyPercentage are hidden */
  minFrequency: PropTypes.number,
  /** Label for the expand button */
  viewMoreLabel: PropTypes.string,
  /** Label for the collapse button */
  viewLessLabel: PropTypes.string,
  /** Additional CSS class for the wrapper */
  className: PropTypes.string,
}

export default WingsSkills
